/**
 * Pattern-based redaction engine.
 * Replaces sensitive data with [REDACTED] before writing reports.
 * Honors Appium 3's X-Appium-Is-Sensitive pattern.
 */

export const DEFAULT_REDACTION_PATTERNS: (string | RegExp)[] = [
  /AKIA[A-Z0-9]{16}/g,
  /Bearer\s+[A-Za-z0-9\-._~+/]+=*/g,
  /-----BEGIN\s+(RSA\s+)?PRIVATE\sKEY-----[\s\S]*?-----END/g,
  /\/\/[^:]+:[^@]+@/g,
];

export const APPIUM_SENSITIVE_COMMANDS = new Set([
  'elementSendKeys',
  'setValueImmediate',
  'replaceValue',
]);

export function createRedactor(
  patterns: (string | RegExp)[],
): (text: string) => string {
  return (text: string): string => {
    let result = text;
    for (const pattern of patterns) {
      if (typeof pattern === 'string') {
        const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        result = result.replace(new RegExp(escaped, 'g'), '[REDACTED]');
      } else {
        const flags = pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g';
        const cloned = new RegExp(pattern.source, flags);
        result = result.replace(cloned, '[REDACTED]');
      }
    }
    return result;
  };
}

export function shouldRedactCommand(commandName: string): boolean {
  return APPIUM_SENSITIVE_COMMANDS.has(commandName);
}

export function redactArgs(args: unknown[], commandName: string): unknown[] {
  if (!shouldRedactCommand(commandName)) return args;
  return args.map((arg) => {
    if (typeof arg === 'string') return '[REDACTED]';
    if (Array.isArray(arg)) return arg.map(() => '[REDACTED]');
    return arg;
  });
}

// ---------------------------------------------------------------------------
// Structured header/body redaction for captured network traffic.
//
// Mirrors the policy in `@testrelic/playwright-analytics` (case-insensitive
// header names, recursive JSON body-field match at any depth). Applied to
// proxy-captured HTTP requests/responses before they are stored or uploaded
// so Authorization/Cookie/Set-Cookie header values and configured body fields
// never leave the machine in the clear.
// ---------------------------------------------------------------------------

const REDACTED = '[REDACTED]';

/** Header names redacted by default (compared case-insensitively). */
export const DEFAULT_REDACT_HEADERS: readonly string[] = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'proxy-authorization',
];

/** JSON body field names redacted by default (compared case-insensitively). */
export const DEFAULT_REDACT_BODY_FIELDS: readonly string[] = [
  'password',
  'secret',
  'token',
  'apiKey',
  'api_key',
  'accessToken',
  'access_token',
];

/**
 * Redact header values whose (case-insensitive) name appears in `redactList`.
 * Returns a new object; the header names are preserved so the shape of the
 * request is still visible, only the sensitive values are masked.
 */
export function redactHeaders(
  headers: Record<string, string> | null,
  redactList: readonly string[] = DEFAULT_REDACT_HEADERS,
): Record<string, string> | null {
  if (headers === null || redactList.length === 0) return headers;
  const lower = new Set(redactList.map((h) => h.toLowerCase()));
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    out[key] = lower.has(key.toLowerCase()) ? REDACTED : value;
  }
  return out;
}

function walkAndRedactBody(value: unknown, fields: ReadonlySet<string>): unknown {
  if (Array.isArray(value)) return value.map((v) => walkAndRedactBody(v, fields));
  if (value === null || typeof value !== 'object') return value;
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    out[key] = fields.has(key.toLowerCase()) ? REDACTED : walkAndRedactBody(val, fields);
  }
  return out;
}

/**
 * Redact JSON body-field values whose (case-insensitive) name appears in
 * `redactList`, recursively at any depth. Non-JSON bodies are returned as-is.
 */
export function redactBodyFields(
  body: string | null,
  redactList: readonly string[] = DEFAULT_REDACT_BODY_FIELDS,
): string | null {
  if (body === null || redactList.length === 0) return body;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    // Not JSON — leave verbatim (form-encoded redaction is a follow-up).
    return body;
  }
  if (typeof parsed !== 'object' || parsed === null) return body;
  const fieldSet = new Set(redactList.map((f) => f.toLowerCase()));
  return JSON.stringify(walkAndRedactBody(parsed, fieldSet));
}
